import { useState, useEffect } from 'react';
import { isEqual } from 'lodash';
import { useTranslation, Card } from '@packages/utils';
import { useTheme } from 'styled-components';
import useForm from 'hooks/useForm';
import PageWrapper from 'components/Base/PageWrapper';
import { Row, Col } from 'components/Base/Grid';
import { Paragraph, Title } from 'components/Base/Typography';
import { Image } from 'components/Base/Image';
import Spinner from 'components/Base/Spinner';
import { Switch } from 'components/Base/Switch';
import { Tabs } from 'components/Base/Tabs';
import { TabsProps } from 'components/Base/Tabs/Tabs.types';
import { Flex } from 'antd';
import {
  useGetLightBoxQuery,
  useUpdateLightBoxMutation,
  useGetELCLightBoxQuery,
  useUpdateELCLightBoxMutation,
  usePublishLightBoxMutation,
  useUnpublishLightBoxMutation,
  usePublishELCLightBoxMutation,
  useUnpublishELCLightBoxMutation,
} from 'redux/apiSlice/lightbox.slice';
import { TRtkErrorType } from 'data/types/generic.types';
import {
  ENotificationType,
  useShowToastNotification,
} from 'components/Base/Notification';
import LightBoxForm from './LightboxForm';
import { SLightBoxCardWrapper } from './LightBox.styles';
import SaveAction from './LightboxActions/SaveAction';
import { TUpdateLightBoxFormUI } from './LightboxForm/LightboxForm.types';

const LightBox = () => {
  const theme = useTheme();

  // RFK Lightbox state and API
  const { data, isLoading, error } = useGetLightBoxQuery();
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [firstSectionText, setFirstSectionText] = useState<string>('');
  const [secondSectionText, setSecondSectionText] = useState<string>('');
  const [isPublished, setIsPublished] = useState<boolean>(false);
  const { form } = useForm<TUpdateLightBoxFormUI>();
  const [updateWorkshopMaintainer, { isLoading: updateIsLoading }] =
    useUpdateLightBoxMutation();
  const [publishLightBox, { isLoading: publishIsLoading }] =
    usePublishLightBoxMutation();
  const [unpublishLightBox, { isLoading: unpublishIsLoading }] =
    useUnpublishLightBoxMutation();

  // ELC Lightbox state and API
  const { data: elcData } = useGetELCLightBoxQuery();
  const [elcSelectedImage, setElcSelectedImage] = useState<string>('');
  const [elcFirstSectionText, setElcFirstSectionText] = useState<string>('');
  const [elcSecondSectionText, setElcSecondSectionText] = useState<string>('');
  const [elcIsPublished, setElcIsPublished] = useState<boolean>(false);
  const { form: elcForm } = useForm<TUpdateLightBoxFormUI>();
  const [updateELCLightBox, { isLoading: elcUpdateIsLoading }] =
    useUpdateELCLightBoxMutation();
  const [publishELCLightBox, { isLoading: elcPublishIsLoading }] =
    usePublishELCLightBoxMutation();
  const [unpublishELCLightBox, { isLoading: elcUnpublishIsLoading }] =
    useUnpublishELCLightBoxMutation();

  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.lightBox',
  });
  const { showToastNotification } = useShowToastNotification();

  // RFK initial values and effects
  const initialValues = {
    firstSection: data?.firstSection || '',
    secondSection: data?.secondSection || '',
    picture: data?.picture || '',
  };

  useEffect(() => {
    if (!data) {
      return;
    }
    setSelectedImage(data.picture);
    setIsPublished(data.published);
  }, [data]);

  const currentData = form.getFieldsValue();

  // ELC initial values and effects
  const elcInitialValues = {
    firstSection: elcData?.firstSection || '',
    secondSection: elcData?.secondSection || '',
    picture: elcData?.picture || '',
  };

  useEffect(() => {
    if (!elcData) {
      return;
    }
    setElcSelectedImage(elcData.picture);
    setElcIsPublished(elcData.published);
    elcForm.setFieldsValue({
      firstSection: elcData.firstSection,
      secondSection: elcData.secondSection,
      picture: elcData.picture,
    });
  }, [elcData, elcForm]);

  const elcCurrentData = elcForm.getFieldsValue();

  if (!data || !elcData) {
    return null;
  }

  // RFK submit handler
  const handleSubmit = async () => {
    try {
      const val = await form.validateFields();
      await updateWorkshopMaintainer({
        id: data.id,
        ...val,
      }).unwrap();
      showToastNotification({
        message: t('actions.save.SUCCESS_MESSAGE'),
        type: ENotificationType.SUCCESS,
      });
    } catch (err) {
      showToastNotification({
        message: (err as TRtkErrorType)?.data?.message,
        type: ENotificationType.ERROR,
      });
    }
  };

  // ELC submit handler
  const handleELCSubmit = async () => {
    try {
      const val = await elcForm.validateFields();
      await updateELCLightBox({
        id: elcData.id,
        ...val,
      }).unwrap();
      showToastNotification({
        message: t('actions.save.SUCCESS_MESSAGE'),
        type: ENotificationType.SUCCESS,
      });
    } catch (err) {
      showToastNotification({
        message: (err as TRtkErrorType)?.data?.message,
        type: ENotificationType.ERROR,
      });
    }
  };

  // RFK publish/unpublish handler
  const handlePublishChange = async (checked: boolean) => {
    setIsPublished(checked);
    try {
      if (checked) {
        await publishLightBox({ lightboxId: data.id }).unwrap();
      } else {
        await unpublishLightBox({ lightboxId: data.id }).unwrap();
      }
      showToastNotification({
        message: checked
          ? t('actions.publish.SUCCESS_MESSAGE')
          : t('actions.unpublish.SUCCESS_MESSAGE'),
        type: ENotificationType.SUCCESS,
      });
    } catch (err) {
      showToastNotification({
        message: (err as TRtkErrorType)?.data?.message,
        type: ENotificationType.ERROR,
      });
      setIsPublished(!checked);
    }
  };

  // ELC publish/unpublish handler
  const handleELCPublishChange = async (checked: boolean) => {
    setElcIsPublished(checked);
    try {
      if (checked) {
        await publishELCLightBox({ lightboxId: elcData.id }).unwrap();
      } else {
        await unpublishELCLightBox({ lightboxId: elcData.id }).unwrap();
      }
      showToastNotification({
        message: checked
          ? t('actions.publish.SUCCESS_MESSAGE')
          : t('actions.unpublish.SUCCESS_MESSAGE'),
        type: ENotificationType.SUCCESS,
      });
    } catch (err) {
      showToastNotification({
        message: (err as TRtkErrorType)?.data?.message,
        type: ENotificationType.ERROR,
      });
      setElcIsPublished(!checked);
    }
  };

  const rfkContent = (
    <Card>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Title level={4} className="mb-3">
            Body
          </Title>
          <LightBoxForm
            form={form}
            initialValues={initialValues}
            selectedImage={selectedImage}
            setSelectedImage={setSelectedImage}
            setFirstSectionText={setFirstSectionText}
            setSecondSectionText={setSecondSectionText}
          />
        </Col>
        <Col xs={24} md={12}>
          <Title level={4} className="mb-3">
            {t('preview.LABEL')}
          </Title>
          <SLightBoxCardWrapper>
            <Image
              src={selectedImage || data.picture}
              alt=""
              className="mb-4"
              aria-hidden="true"
            />
            <Paragraph
              className="text-center"
              fontWeight={theme.fontWeightMedium}
              color={theme.colorPinkActive}
            >
              {firstSectionText || data.firstSection}
            </Paragraph>
            <Paragraph className="text-center my-4">
              {secondSectionText || data.secondSection}
            </Paragraph>
            <Paragraph className="text-center">
              {t('preview.contact_content.CONTENT1')}{' '}
              <a
                href="https://readyforkindergartenidaho.org/"
                className="word-wrap-break-word"
              >
                {t('preview.contact_content.WEBSITE')}{' '}
              </a>
              {t('preview.contact_content.CONTENT2')}
            </Paragraph>
          </SLightBoxCardWrapper>
          <Flex justify="flex-end" align="center" gap={16} className="mt-3">
            <Paragraph className="mb-0">Show Message</Paragraph>
            <Switch
              checked={isPublished}
              onChange={handlePublishChange}
              loading={publishIsLoading || unpublishIsLoading}
              checkedChildren="ON"
              unCheckedChildren="OFF"
            />
            <SaveAction
              formHasChange={!isEqual(currentData, initialValues)}
              isLoading={updateIsLoading}
              handleSubmit={handleSubmit}
            />
          </Flex>
        </Col>
      </Row>
    </Card>
  );

  const elcContent = (
    <Card>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Title level={4} className="mb-3">
            Body
          </Title>
          <LightBoxForm
            form={elcForm}
            initialValues={elcInitialValues}
            selectedImage={elcSelectedImage}
            setSelectedImage={setElcSelectedImage}
            setFirstSectionText={setElcFirstSectionText}
            setSecondSectionText={setElcSecondSectionText}
            isELC
          />
        </Col>
        <Col xs={24} md={12}>
          <Title level={4} className="mb-3">
            {t('preview.LABEL')}
          </Title>
          <SLightBoxCardWrapper>
            <Image
              src={elcSelectedImage || elcData.picture}
              alt=""
              className="mb-4"
              aria-hidden="true"
            />
            <Paragraph
              className="text-center"
              fontWeight={theme.fontWeightMedium}
              color={theme.colorPinkActive}
            >
              {elcFirstSectionText || elcData.firstSection}
            </Paragraph>
            <Paragraph className="text-center my-4">
              {elcSecondSectionText || elcData.secondSection}
            </Paragraph>
          </SLightBoxCardWrapper>
          <Flex justify="flex-end" align="center" gap={16} className="mt-3">
            <Paragraph className="mb-0">Show Message</Paragraph>
            <Switch
              checked={elcIsPublished}
              onChange={handleELCPublishChange}
              loading={elcPublishIsLoading || elcUnpublishIsLoading}
              checkedChildren="ON"
              unCheckedChildren="OFF"
            />
            <SaveAction
              formHasChange={!isEqual(elcCurrentData, elcInitialValues)}
              isLoading={elcUpdateIsLoading}
              handleSubmit={handleELCSubmit}
            />
          </Flex>
        </Col>
      </Row>
    </Card>
  );

  const tabItems: TabsProps['items'] = [
    {
      key: 'rfk',
      label: 'RFK',
      children: rfkContent,
    },
    {
      key: 'elc',
      label: 'ELC',
      children: elcContent,
    },
  ];

  return (
    <PageWrapper title="Settings / Messaging" error={error as TRtkErrorType}>
      {isLoading ? (
        <Spinner spinnerSize={16} />
      ) : (
        <Tabs defaultActiveKey="rfk" items={tabItems} />
      )}
    </PageWrapper>
  );
};

export default LightBox;
